/**
 * THE CLOCK A PAGE'S DATA IS DRAWN BY — normally now; the last reading's moment once the reader
 * chose "Show last readings" on a site that stopped reporting (Hamzah, 2026-10-14).
 *
 * "Today" on these pages is computed in a dozen places, each from its own `useNow()`: the Overview's
 * power curve, its kWh ring, each plant's yield, the grid's day, a device's last 24 hours. A silent
 * site has nothing today, so the button that promised its last readings showed empty charts. Rather
 * than thread a second clock through every one, a page that chose it is wrapped in this context by
 * `SilenceRevealProvider`, and every hook that picks WHICH DAY'S
 * DATA to fetch reads `useDataNow()` instead of `useNow()` — so Oct 9 is drawn by the same code that
 * draws today, with no second path to drift from it.
 *
 * `useNow()` itself is untouched and stays the wall clock: an alarm's age, "nothing since Oct 9
 * (5d)", a work order's due time are about NOW whatever day the charts show.
 */
import { createContext, useContext } from 'react';
import { useNow } from '@/lib/use-now';

/** A page moved to its last reading: the clock, and the words for it in the site's own zone. */
export interface PastClock {
  /** The data clock: one minute past the last reading. */
  atMs: number;
  /** "Oct 9" — where a page says "today". */
  day: string;
  /** "Oct 9, 3:54 PM" — where a page says "now". */
  at: string;
}

/** Provided by `SilenceRevealProvider` (`lib/SilenceRevealProvider.tsx`); null is the live clock. */
export const DataClock = createContext<PastClock | null>(null);

/** The instant whose day a chart or a figure should show. */
export function useDataNow(intervalMs = 60_000): number {
  const live = useNow(intervalMs);
  const fixed = useContext(DataClock);
  return fixed?.atMs ?? live;
}

/**
 * The words a card puts where it would say "today" and "now", or null while the page shows today.
 * `day` replaces "today" ("kWh, Oct 9"), `at` replaces "now" ("Output, Oct 9, 3:54 PM").
 */
export function useDataPast(): PastClock | null {
  return useContext(DataClock);
}
